import { queryOptions, useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { isNativeApp } from "@/lib/platform";

/**
 * Where the public website lives, once it is a separate site from the app.
 *
 * The server writes it into a fresh page as `<meta name="gridaction-website" content="https://gridaction.app">` (from the WEBSITE_URL
 * setting, apps/api/src/http/web.ts). A phone or browser that kept a saved copy of the app's page (the service worker) never sees that
 * tag, so the page also asks the server (`GET /api/v1/site`) when the tag is missing. With neither, the app is still serving its own
 * home page, Help, blog and legal pages, and nothing here does anything. The phone apps never leave for the website.
 *
 * A website address that is the address this page is on counts as no website: the app is then being served as the website (on the
 * switch evening, between setting WEBSITE_URL and pointing the domain at the website, or after the domain is pointed back), and handing
 * a page over to the address it is already on would reload it for ever.
 */
function elsewhere(address: string | null | undefined): string | null {
  const clean = address?.trim().replace(/\/+$/, "");
  if (!clean) return null;
  try {
    return new URL(clean).origin === window.location.origin ? null : clean;
  } catch {
    return null;
  }
}

function tag(): string | undefined {
  if (typeof document === "undefined") return undefined;
  return document.querySelector<HTMLMetaElement>('meta[name="gridaction-website"]')?.content;
}

export function websiteUrl(): string | null {
  return isNativeApp ? null : elsewhere(tag());
}

export const siteQuery = queryOptions({
  queryKey: ["site"],
  queryFn: async () => {
    const { websiteUrl: address } = await api.get<{ websiteUrl: string | null }>("/site");
    return elsewhere(address);
  },
  staleTime: 5 * 60_000,
  retry: false,
});

/** The website's address (from the page's tag, else from the server), or null while it is unknown or there is no separate website. */
export function useWebsiteUrl(): string | null {
  const { data } = useQuery({ ...siteQuery, enabled: !isNativeApp && !tag()?.trim() });
  return websiteUrl() ?? data ?? null;
}
